import { ComponentProps, FC, ReactNode, useMemo, useState } from 'react'

import { useFragment } from '@apollo/client'
import { F0Box } from '@factorialco/f0-react'
import { DetailsItemsList, Tabs } from '@factorialco/f0-react/dist/experimental'
import { ProcurementsApprovalTimelineDocument } from 'generated/resources/graphql'
import { ProcurementPurchaseRequestApprovableFragmentDoc } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'
import { contactFullName } from 'modules/finance/lib/contactsUtils'

import { EmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'
import DetailsTab from './DetailsTab'
import PurchaseRequestWorkflowTimeline from 'modules/procurement/components/purchaseRequests/PurchaseRequestWorkflowTimeline'

const TABS = [
  {
    id: 'details',
    label: () => i18n.t('common.details'),
  },
  {
    id: 'timeline',
    label: () => i18n.t('performance_process.reviews.timeline'),
  },
]

const TABS_WRAPPER_STYLE = {
  margin: '0 -24px',
}

type ProcurementPurchaseRequestTaskContentProps = {
  id: string
  requesterContent: EmployeeTableViewRow
  detailsContent?: ReactNode
}

const ProcurementPurchaseRequestTaskContent: FC<ProcurementPurchaseRequestTaskContentProps> = ({
  id,
  requesterContent,
  detailsContent,
}) => {
  const {
    access: { locale: accessLocale },
    company,
    employee,
  } = useCurrentSession()

  const { navigation } = useNavigation()

  const { data } = useFragment({
    fragment: ProcurementPurchaseRequestApprovableFragmentDoc,
    fragmentName: 'ProcurementPurchaseRequestApprovable',
    from: {
      __typename: 'ProcurementPurchaseRequest',
      id,
    },
    optimistic: false,
  })

  const { data: timelineData } = useQuery(ProcurementsApprovalTimelineDocument, {
    variables: {
      companyId: company.id,
      purchaseRequestId: data?.id ?? '0',
    },
    skip: !data?.id,
  })

  const timelineEntries =
    timelineData?.procurement?.approvalsTimelinesConnection?.nodes?.[0]?.entries ?? []

  const purchaseRequestType = data?.purchaseRequestType?.name

  const costText = data?.cost
    ? MoneyWithCurrency({
        amount: Number(data.cost.cents),
        currency: data.cost.currency,
        locale: accessLocale,
      })
    : undefined

  const summary = data?.purchaseRequestDescription
  const vendorName = data?.vendor ? contactFullName(data.vendor) : undefined

  const details: ComponentProps<typeof DetailsItemsList>['details'] = useMemo(
    () =>
      [
        summary && {
          title: i18n.t('common.title'),
          content: { text: summary, type: 'item' as const },
        },
        costText && {
          title: i18n.t('banking.payments.detail_total_amount'),
          content: { text: costText, type: 'item' as const },
        },
        data?.cost?.currency && {
          title: i18n.t('expenses_v2.forms.fields.currency.label'),
          content: { text: data.cost.currency, type: 'item' as const },
        },
        requesterContent,
        {
          title: i18n.t('procurement.purchase_requests.detail.info.type'),
          content: { text: purchaseRequestType ?? '-', type: 'item' as const },
        },
        {
          title: i18n.t('procurement.purchase_requests.list.vendor'),
          content: { text: vendorName ?? '-', type: 'item' as const },
        },
        data?.id && {
          title: i18n.t('procurement.purchase_requests.detail.info.purchase_request'),
          content: {
            text: summary ?? `#${data.id}`,
            type: 'item' as const,
            action: {
              type: 'navigate' as const,
              href:
                employee.id === data.requesterEmployee?.id
                  ? navigation.myPurchases.detail.fullPath({ id: data.id })
                  : navigation.procurement.purchaseRequests.detail.fullPath({ id: data.id }),
            },
          },
        },
      ].filter(Boolean),
    [
      summary,
      costText,
      data?.cost?.currency,
      requesterContent,
      purchaseRequestType,
      vendorName,
      data?.id,
      data?.requesterEmployee?.id,
      employee.id,
      navigation,
    ]
  )

  const files = useMemo(
    () =>
      data?.files
        ?.map(
          (file) =>
            file?.filename &&
            file?.url && {
              name: file.filename,
              type: file.contentType ?? undefined,
              url: file.url,
            }
        )
        .filter(Boolean) ?? [],
    [data?.files]
  )

  const tabs = useMemo(
    () =>
      TABS.map((tab) => ({
        ...tab,
        label: tab.label(),
      })),
    []
  )

  const [activeTabId, setActiveTabId] = useState<string | undefined>(tabs?.[0]?.id)

  if (!data) return null

  return (
    <F0Box display="flex" flexDirection="column" gap="xl" overflowX="hidden">
      <div style={TABS_WRAPPER_STYLE}>
        <Tabs tabs={tabs} activeTabId={activeTabId} setActiveTabId={setActiveTabId} />
      </div>
      <F0Box width="full" display={activeTabId !== 'details' ? 'none' : 'block'}>
        <DetailsTab
          details={details}
          files={files}
          additionalInformation={data.additionalInformation}
          detailsContent={detailsContent}
        />
      </F0Box>
      <F0Box width="full" display={activeTabId !== 'timeline' ? 'none' : 'block'}>
        <PurchaseRequestWorkflowTimeline
          approvalProcess={
            data.approvalProcess
              ? {
                  ...data.approvalProcess,
                  reviewRequests: (data.approvalProcess.reviewRequests ?? []).filter(
                    (r) => r != null
                  ),
                }
              : null
          }
          timelineEntries={timelineEntries}
        />
      </F0Box>
    </F0Box>
  )
}

export default ProcurementPurchaseRequestTaskContent

export * from './metadata'
